/**
 * PlaylistEnergySparkline — mini gráfico de energía (BPM) de una playlist
 *
 * Muestra la curva de BPM canción a canción con un SVG sparkline compacto.
 * Útil para que el DJ vea de un vistazo el flow de la playlist: aperturas
 * suaves, picos, valles, cierres altos.
 *
 * Features:
 *   - Puntos sin BPM se saltan (no se consideran para la línea)
 *   - Color del trazo varía según tendencia (media creciente = naranja,
 *     decreciente = azul, mixto = gradiente cian→naranja)
 *   - Hover sobre la zona muestra tooltip con stats (min, max, avg, %cobertura)
 *   - Badges rápidos: "🔥 cierre potente" si los últimos 3 suben, etc.
 *
 * Uso:
 *   <PlaylistEnergySparkline songs={generatedCustomList} />
 */

import React, { useMemo } from 'react';
import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const WIDTH = 220;
const HEIGHT = 44;
const PAD_X = 4;
const PAD_Y = 6;

export const PlaylistEnergySparkline = ({ songs = [] }) => {
  const stats = useMemo(() => {
    if (!Array.isArray(songs) || songs.length === 0) return null;
    const bpms = songs.map((s) => (s?.bpm > 0 ? s.bpm : null));
    const validCount = bpms.filter((b) => b !== null).length;
    if (validCount < 2) return null;
    const valid = bpms.filter((b) => b !== null);
    const min = Math.min(...valid);
    const max = Math.max(...valid);
    const avg = Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
    const coverage = Math.round((validCount / songs.length) * 100);
    // Tendencia de los últimos 3 con BPM
    const lastThree = valid.slice(-3);
    let cierre = 'neutral';
    if (lastThree.length === 3) {
      if (lastThree[2] > lastThree[0] + 5) cierre = 'up';
      else if (lastThree[2] < lastThree[0] - 5) cierre = 'down';
    }
    // Tendencia global
    const firstHalf = valid.slice(0, Math.floor(valid.length / 2));
    const secondHalf = valid.slice(Math.floor(valid.length / 2));
    const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
    const globalTrend =
      avgSecond > avgFirst + 3 ? 'up' :
      avgSecond < avgFirst - 3 ? 'down' : 'flat';
    return { bpms, min, max, avg, coverage, cierre, globalTrend, validCount, total: songs.length };
  }, [songs]);

  if (!stats) {
    return null;
  }

  const { bpms, min, max, avg, coverage, cierre, globalTrend, validCount, total } = stats;

  // Construir los puntos del sparkline
  const xStep = (WIDTH - PAD_X * 2) / Math.max(1, bpms.length - 1);
  const yRange = Math.max(1, max - min);
  const points = bpms.map((bpm, i) => {
    if (bpm === null) return null;
    const x = PAD_X + i * xStep;
    const y = HEIGHT - PAD_Y - ((bpm - min) / yRange) * (HEIGHT - PAD_Y * 2);
    return { x, y, bpm, idx: i };
  });

  // Path SVG (saltando nulls con "M" para crear sub-trazos)
  let path = '';
  let inSegment = false;
  points.forEach((p) => {
    if (p === null) { inSegment = false; return; }
    path += inSegment ? ` L ${p.x.toFixed(1)} ${p.y.toFixed(1)}` : ` M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
    inSegment = true;
  });

  const trendColor =
    globalTrend === 'up' ? '#fb923c' :       // naranja
    globalTrend === 'down' ? '#60a5fa' :     // azul
    '#22d3ee';                                // cian para mixto/flat

  const cierreBadge =
    cierre === 'up' ? { text: '🔥 Cierre potente', cls: 'text-orange-300 bg-orange-500/20 border-orange-500/40' } :
    cierre === 'down' ? { text: '🌙 Cierre calmado', cls: 'text-blue-300 bg-blue-500/20 border-blue-500/40' } :
    null;

  const trendBadge =
    globalTrend === 'up' ? { icon: TrendingUp, text: 'Energía creciente', cls: 'text-orange-300' } :
    globalTrend === 'down' ? { icon: TrendingDown, text: 'Energía decreciente', cls: 'text-blue-300' } :
    { icon: Minus, text: 'Energía estable', cls: 'text-cyan-300' };

  const tooltip = `BPM min: ${min} · max: ${max} · avg: ${avg}\nCobertura: ${validCount}/${total} (${coverage}%)`;

  return (
    <div
      className="flex items-center gap-3 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2 min-w-0"
      data-testid="playlist-energy-sparkline"
      title={tooltip}
    >
      <Activity className="w-4 h-4 text-green-400 shrink-0" />
      <svg
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="overflow-visible flex-1 min-w-0 max-w-[220px] h-11"
        aria-label="Curva de BPM de la playlist"
      >
        {/* Línea de referencia: avg */}
        <line
          x1={PAD_X}
          x2={WIDTH - PAD_X}
          y1={HEIGHT - PAD_Y - ((avg - min) / yRange) * (HEIGHT - PAD_Y * 2)}
          y2={HEIGHT - PAD_Y - ((avg - min) / yRange) * (HEIGHT - PAD_Y * 2)}
          stroke="#475569"
          strokeDasharray="2 2"
          strokeWidth="0.5"
        />
        {/* Trazo principal */}
        <path
          d={path}
          stroke={trendColor}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Puntos: solo primero y último */}
        {points.length > 0 && points[0] && (
          <circle cx={points[0].x} cy={points[0].y} r="2" fill={trendColor} />
        )}
        {points.length > 0 && points[points.length - 1] && (
          <circle
            cx={points[points.length - 1].x}
            cy={points[points.length - 1].y}
            r="2.5"
            fill={trendColor}
            stroke="white"
            strokeWidth="0.8"
          />
        )}
      </svg>
      <div className="flex flex-col gap-0.5 text-xs min-w-0">
        <span className={`flex items-center gap-1 font-medium ${trendBadge.cls}`}>
          <trendBadge.icon className="w-3 h-3" />
          {trendBadge.text}
        </span>
        <span className="text-slate-500 truncate">
          {min}–{max} BPM · avg {avg} · {coverage}%
        </span>
      </div>
      {cierreBadge && (
        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${cierreBadge.cls} shrink-0`}>
          {cierreBadge.text}
        </span>
      )}
    </div>
  );
};

export default PlaylistEnergySparkline;
